import React, { useState, useEffect } from 'react';
import { 
  Award, 
  ShieldCheck, 
  Lock, 
  Eye, 
  EyeOff, 
  KeyRound, 
  Check, 
  Copy, 
  AlertTriangle, 
  RefreshCw, 
  Clock, 
  ExternalLink,
  Zap,
  TrendingUp,
  Building2,
  Trash2,
  CheckCircle2,
  Info
} from 'lucide-react';
import { 
  getCohortSettings, 
  saveCohortSettings, 
  grantDeepAuditPass, 
  revokeDeepAuditPass, 
  leaveCohort, 
  CohortAccessSettings,
  KNOWN_COHORT_PRESETS
} from '../utils/cohortService';

interface CohortGovernancePanelProps {
  showToast?: (msg: string, type?: 'info' | 'success' | 'warning' | 'error') => void;
  onOpenCohortOnboarding?: () => void;
}

export function CohortGovernancePanel({ showToast, onOpenCohortOnboarding }: CohortGovernancePanelProps) {
  const [settings, setSettings] = useState<CohortAccessSettings>(getCohortSettings());
  const [copiedToken, setCopiedToken] = useState(false);

  useEffect(() => {
    const handleUpdate = (e: any) => {
      setSettings(e.detail || getCohortSettings());
    };
    window.addEventListener('cohortSettingsUpdated', handleUpdate);
    return () => window.removeEventListener('cohortSettingsUpdated', handleUpdate);
  }, []);

  const handleToggleTelemetry = () => {
    const updated: CohortAccessSettings = {
      ...settings,
      sharingEnabled: !settings.sharingEnabled
    };
    saveCohortSettings(updated);
    setSettings(updated);
    if (showToast) {
      showToast(
        updated.sharingEnabled 
          ? '🟢 Cohort runway health sharing activated.' 
          : '🔴 Cohort runway health sharing paused.',
        'info'
      );
    }
  };

  const handleToggleRunwayBands = () => {
    const updated: CohortAccessSettings = {
      ...settings,
      shareRunwayBands: !settings.shareRunwayBands
    };
    saveCohortSettings(updated);
    setSettings(updated);
  };

  const handleToggleTaxStatus = () => {
    const updated: CohortAccessSettings = {
      ...settings,
      shareTaxComplianceStatus: !settings.shareTaxComplianceStatus
    };
    saveCohortSettings(updated);
    setSettings(updated);
  };

  const handleGrantAuditPass = () => {
    const updated = grantDeepAuditPass(30);
    setSettings(updated);
    if (showToast) showToast('🔑 30-Day Director Audit Pass generated!', 'success');
  };

  const handleRevokeAuditPass = () => {
    const updated = revokeDeepAuditPass();
    setSettings(updated);
    if (showToast) showToast('🔒 Deep Audit Pass revoked immediately.', 'warning');
  };

  const handleCopyPass = () => {
    if (settings.deepAuditPassToken) {
      navigator.clipboard.writeText(settings.deepAuditPassToken);
      setCopiedToken(true);
      if (showToast) showToast('📋 Audit pass token copied to clipboard!', 'success');
      setTimeout(() => setCopiedToken(false), 3000);
    }
  };

  const handleLeaveCohort = () => {
    const reset = leaveCohort();
    setSettings(reset);
    if (showToast) showToast('Disconnected from cohort sponsorship.', 'info');
  };

  if (!settings.isCohortMember) {
    return (
      <div className="p-6 text-center space-y-4 rounded-2xl bg-[#0C0E0F] border border-[#2A2F31]">
        <div className="w-12 h-12 rounded-2xl bg-teal-500/10 border border-teal-500/20 text-teal-400 flex items-center justify-center mx-auto">
          <Award size={24} />
        </div>
        <div className="space-y-1">
          <h4 className="text-base font-bold text-white">Join an Accelerator Cohort</h4>
          <p className="text-xs text-gray-400 max-w-md mx-auto">
            Are you part of an accelerator, venture studio, or incubator program? Enter your institutional voucher code to activate your sponsored license and cohort health sync.
          </p>
        </div>
        <button
          type="button"
          onClick={onOpenCohortOnboarding}
          className="px-5 py-2.5 rounded-xl bg-teal-500 hover:bg-teal-400 text-[#0C0E0F] text-xs font-bold transition-all shadow-lg shadow-teal-500/20 cursor-pointer"
        >
          Enter Cohort Voucher Code
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      
      {/* COHORT SPONSORSHIP STATUS CARD */}
      <div className="p-4 sm:p-5 rounded-2xl bg-[#0C0E0F] border border-teal-500/30 space-y-4 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-teal-500/5 rounded-full blur-2xl pointer-events-none" />

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-[#1F2426] pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-teal-500/15 border border-teal-500/30 text-teal-400 flex items-center justify-center shrink-0">
              <Award size={20} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-bold text-white">{settings.cohortName}</h4>
                <span className="text-[10px] px-2 py-0.5 rounded bg-teal-500/20 text-teal-300 font-mono font-bold">
                  Active Sublicense
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-0.5">
                Sponsored by <strong className="text-gray-200">{settings.sponsorName}</strong>
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {onOpenCohortOnboarding && (
              <button
                type="button"
                onClick={onOpenCohortOnboarding}
                className="px-3 py-1.5 rounded-lg border border-teal-500/30 hover:bg-teal-950/30 text-teal-300 text-xs font-medium transition-colors flex items-center gap-1.5 cursor-pointer"
              >
                <Award size={12} />
                <span>Change Voucher</span>
              </button>
            )}
            <button
              type="button"
              onClick={handleLeaveCohort}
              className="px-3 py-1.5 rounded-lg border border-red-500/30 hover:bg-red-950/30 text-red-400 text-xs font-medium transition-colors flex items-center gap-1.5 cursor-pointer"
            >
              <Trash2 size={12} />
              <span>Leave Cohort</span>
            </button>
          </div>
        </div>

        {/* PERMISSION CONTROLS (THE GOVERNANCE CORE) */}
        <div className="space-y-3">
          <div className="text-xs font-bold text-gray-300 uppercase tracking-wider">
            Cohort Data Governance &amp; Sharing Controls
          </div>

          {/* TOGGLE 1: MASTER RUNWAY SHARING (ON BY DEFAULT) */}
          <div className="p-3.5 rounded-xl bg-[#151819] border border-[#2A2F31] flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-white">Cohort Health Telemetry</span>
                <span className={`text-[10px] px-1.5 py-0.2 rounded font-mono ${settings.sharingEnabled ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'}`}>
                  {settings.sharingEnabled ? 'Active (Default)' : 'Paused'}
                </span>
              </div>
              <p className="text-[11px] text-gray-400">
                Pushes high-level runway traffic lights and burn bands to the accelerator heatmap. Raw transactions remain 100% private.
              </p>
            </div>
            <button
              type="button"
              onClick={handleToggleTelemetry}
              className={`w-12 h-6 rounded-full p-0.5 transition-colors cursor-pointer shrink-0 ${
                settings.sharingEnabled ? 'bg-teal-500' : 'bg-gray-700'
              }`}
            >
              <div className={`w-5 h-5 rounded-full bg-white transition-transform ${
                settings.sharingEnabled ? 'translate-x-6' : 'translate-x-0'
              }`} />
            </button>
          </div>

          {/* SUB-TOGGLES */}
          {settings.sharingEnabled && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pl-2 border-l-2 border-teal-500/30">
              <label className="p-2.5 rounded-lg bg-[#151819] border border-[#2A2F31] flex items-center justify-between cursor-pointer">
                <span className="text-[11px] text-gray-300">Runway Month Bands (Green/Yellow/Red)</span>
                <input
                  type="checkbox"
                  checked={settings.shareRunwayBands}
                  onChange={handleToggleRunwayBands}
                  className="accent-teal-500 cursor-pointer"
                />
              </label>

              <label className="p-2.5 rounded-lg bg-[#151819] border border-[#2A2F31] flex items-center justify-between cursor-pointer">
                <span className="text-[11px] text-gray-300">Statutory Tax &amp; Audit Readiness</span>
                <input
                  type="checkbox"
                  checked={settings.shareTaxComplianceStatus}
                  onChange={handleToggleTaxStatus}
                  className="accent-teal-500 cursor-pointer"
                />
              </label>
            </div>
          )}

          {/* TOGGLE 2: DEEP AUDIT ROOM PASS (OFF BY DEFAULT) */}
          <div className="p-3.5 rounded-xl bg-[#151819] border border-purple-500/30 space-y-3">
            <div className="flex items-center justify-between gap-4">
              <div className="space-y-0.5">
                <div className="flex items-center gap-2">
                  <KeyRound size={14} className="text-purple-400" />
                  <span className="text-xs font-bold text-white">Director Deep-Audit Room</span>
                  <span className={`text-[10px] px-1.5 py-0.2 rounded font-mono ${settings.deepAuditEnabled ? 'bg-purple-500/20 text-purple-300' : 'bg-gray-800 text-gray-400'}`}>
                    {settings.deepAuditEnabled ? 'Unlocked (30 Days)' : 'Locked (Default)'}
                  </span>
                </div>
                <p className="text-[11px] text-gray-400">
                  Allows program directors to inspect categorized P&L curves and expense breakdowns during hands-on bridge or grant reviews.
                </p>
              </div>

              {settings.deepAuditEnabled ? (
                <button
                  type="button"
                  onClick={handleRevokeAuditPass}
                  className="px-3 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-300 text-xs font-bold border border-red-500/40 transition-colors shrink-0 cursor-pointer"
                >
                  Revoke Pass
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleGrantAuditPass}
                  className="px-3 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold transition-colors shrink-0 cursor-pointer shadow-md shadow-purple-900/30"
                >
                  Grant 30-Day Pass
                </button>
              )}
            </div>

            {settings.deepAuditEnabled && (
              <div className="p-2.5 rounded-lg bg-[#0C0E0F] border border-purple-500/40 flex items-center justify-between gap-2">
                <div className="space-y-0.5 truncate">
                  <div className="text-[10px] text-purple-300 font-mono">ACTIVE DECRYPTION TOKEN:</div>
                  <div className="text-xs font-mono font-bold text-white truncate">{settings.deepAuditPassToken}</div>
                </div>
                <button
                  type="button"
                  onClick={handleCopyPass}
                  className="px-2.5 py-1 rounded bg-purple-950/50 hover:bg-purple-900/50 text-purple-300 border border-purple-500/40 text-[11px] font-mono flex items-center gap-1 shrink-0 cursor-pointer"
                >
                  {copiedToken ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
                  <span>{copiedToken ? 'Copied' : 'Copy'}</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* LIVE SIMULATION: WHAT THE ACCELERATOR SEES RIGHT NOW */}
        <div className="p-3.5 rounded-xl bg-[#151819] border border-[#2A2F31] space-y-2.5">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-gray-300 uppercase tracking-wider flex items-center gap-1.5">
              <Eye size={12} className="text-teal-400" />
              <span>Live Accelerator Drill-Down Preview</span>
            </span>
            <span className="text-[10px] text-gray-500 font-mono">Real-Time Radar</span>
          </div>

          <div className="p-3 rounded-lg bg-[#0C0E0F] border border-[#2A2F31] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-white">Your Startup</span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/20 text-emerald-400">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  🟢 Safe Runway (8.4 Months)
                </span>
              </div>
              <div className="text-[11px] text-gray-400 flex items-center gap-3">
                <span>Burn: <strong>$2,450/mo</strong></span>
                <span>Tax Reserves: <strong className="text-emerald-400">100% Ring-Fenced</strong></span>
                <span>Audit Room: <strong className={settings.deepAuditEnabled ? 'text-purple-400' : 'text-gray-500'}>
                  {settings.deepAuditEnabled ? 'Unlocked' : 'Locked'}
                </strong></span>
              </div>
            </div>

            <span className="text-[10px] text-teal-400 font-mono px-2 py-1 rounded bg-teal-500/10 border border-teal-500/20">
              {settings.sharingEnabled ? '📡 Telemetry Active' : '⏸️ Sharing Paused'}
            </span>
          </div>
        </div>

      </div>
    </div>
  );
}
